import React, { memo } from 'react';
import { formatDistanceToNow } from 'date-fns';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ImageWithFallback from '../../../components/ImageWithFallback';

const JobDetailsHeader = memo(({
  job,
  isBookmarked,
  hasApplied,
  applicationStatus,
  isJobExpired,
  onApply,
  onBookmark,
  onReport,
  onEditJob,
  formatSalary,
  getSalaryTooltipText,
  getJobTypeLabel,
  getExperienceLabel,
  userProfile
}) => {
  const isOwner = userProfile?.id && (
    (job?.employer_id && userProfile.id === job.employer_id) ||
    (job?.employer?.id && userProfile.id === job.employer.id)
  );
  return (
    <>
      {/* Job Header */}
      <header className="flex flex-wrap items-center flex-col lg:flex-row lg:gap-6 mb-8">
        {/* Company Logo */}
        <div className="w-40 h-40 rounded-xl overflow-hidden bg-secondary-100 flex-shrink-0 mb-6 lg:mb-0">
          <ImageWithFallback
            src={job?.employer?.company_logo || job?.employer?.profile_picture}
            fallbackSrc="/assets/images/Logo/webp/VAS-LOGO.webp"
            alt={job?.employer?.company_name || 'Company'}
            className="w-full h-full object-contain"
            lazy={false}
          />
        </div>

        {/* Job Info */}
        <div className="flex-1 min-w-0">
          <h1 className="text-3xl font-bold text-text-primary mb-2 text-center lg:text-left">
            {job?.title}
          </h1>
          <div className="flex items-center justify-center lg:justify-start space-x-4 mb-4">
            <span className="text-lg text-primary font-semibold">
              {job?.company_name}
            </span>
            <div className="flex items-center space-x-1 text-text-secondary">
              <Icon name="MapPin" size={16} />
              <span>{job?.location}</span>
            </div>
            {job?.is_remote && (
              <span className="bg-accent-100 text-accent-700 text-sm px-3 py-1 rounded-full">
                Remote
              </span>
            )}
          </div>

          {/* Job Meta */}
          <div className="flex flex-wrap justify-center lg:justify-start items-center gap-4 text-sm text-text-secondary">
            <div className="flex items-center space-x-1">
              <Icon name="Briefcase" size={16} />
              <span>{getJobTypeLabel(job?.job_type)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="TrendingUp" size={16} />
              <span>{getExperienceLabel(job?.experience_level)}</span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={16} />
              <span>
                Posted {job?.created_at 
                  ? formatDistanceToNow(new Date(job.created_at), { addSuffix: true })
                  : 'recently'
                }
              </span>
            </div>
            {job?.applications_count !== undefined && (
              <div className="flex items-center space-x-1">
                <Icon name="Users" size={16} />
                <span>{job.applications_count} applicants</span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-3 mt-6 lg:mt-0">
          <div className="text-right hidden lg:block">
            <div 
              className="text-2xl font-bold text-text-primary cursor-help" 
              title={getSalaryTooltipText(job?.budget_min, job?.budget_max, job?.job_type, job?.currency)}
            >
              {formatSalary(job?.budget_min, job?.budget_max, job?.job_type, job?.currency)}
            </div>
            <div className="text-sm text-text-secondary">
              Monthly estimate
              {userProfile?.preferred_currency && job?.currency && userProfile.preferred_currency !== job.currency && (
                <span className="block text-xs text-text-muted">
                  Converted from {job.currency}
                </span>
              )}
            </div>
          </div>

          {/* Report Flag Button */}
          <button
            onClick={onReport}
            className="p-3 rounded-lg border border-border hover:bg-secondary-50 hover:border-red-300 transition-colors group"
            title="Report this job"
          >
            <Icon 
              name="Flag" 
              size={20} 
              className="text-text-muted group-hover:text-red-500 transition-colors" 
            />
          </button>

          <button
            onClick={onBookmark}
            className="p-3 rounded-lg border border-border hover:bg-secondary-50 transition-colors"
          >
            <Icon 
              name={isBookmarked ? "Bookmark" : "BookmarkPlus"} 
              size={20} 
              className={isBookmarked ? "text-warning" : "text-text-muted"} 
            />
          </button>

          <div className="flex flex-col items-end space-y-2">
            {/* Job owner sees Edit button; VAs/others see Apply button */}
            {isOwner && typeof onEditJob === 'function' ? (
              <Button
                variant="primary"
                size="lg"
                iconName="Edit2"
                onClick={onEditJob}
                title="Edit this job posting"
              >
                Edit Job
              </Button>
            ) : userProfile?.role !== 'employer' && (
              <div className="flex flex-col md:flex-row gap-2">
                {isJobExpired ? (
                  <Button
                    variant="secondary"
                    size="lg"
                    iconName="Clock"
                    disabled={true}
                    title="This job posting has expired"
                  >
                    Expired
                  </Button>
                ) : (
                  <Button
                    variant={hasApplied ? "secondary" : "primary"}
                    size="lg"
                    onClick={onApply}
                    iconName={hasApplied ? "Check" : "Send"}
                    disabled={hasApplied}
                    title={hasApplied ? "You have already applied to this job" : "Apply to this job"}
                  >
                    {hasApplied ? "Applied" : "Apply Now"}
                  </Button>
                )}
              </div>
            )}

            {isJobExpired && (
              <span className="text-sm px-2 py-1 rounded-full bg-red-100 text-red-700">
                Application deadline has passed
              </span>
            )}
            
            {hasApplied && applicationStatus && (
              <span className={`absolute translate-x-0 -translate-y-5 text-xs px-2 py-1 rounded-full capitalize ${
                applicationStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                applicationStatus === 'shortlisted' ? 'bg-blue-100 text-blue-800' :
                applicationStatus === 'interviewed' ? 'bg-purple-100 text-purple-800' :
                applicationStatus === 'accepted' ? 'bg-green-100 text-green-800' :
                applicationStatus === 'rejected' ? 'bg-red-100 text-red-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                Status: {applicationStatus}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Salary */}
      <div className="lg:hidden mt-6 pt-6 border-t border-border mb-8">
        <div className="text-center">
          <div 
            className="text-2xl font-bold text-text-primary cursor-help" 
            title={getSalaryTooltipText(job?.budget_min, job?.budget_max, job?.job_type, job?.currency)}
          >
            {formatSalary(job?.budget_min, job?.budget_max, job?.job_type, job?.currency)}
          </div>
          <div className="text-sm text-text-secondary">
            Monthly estimate
            {userProfile?.preferred_currency && job?.currency && userProfile.preferred_currency !== job.currency && (
              <span className="block text-xs text-text-muted">
                Converted from {job.currency}
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
});

JobDetailsHeader.displayName = 'JobDetailsHeader';

export default JobDetailsHeader;